import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import useTranslation from "../../../../../../packages/locales/translations/util/useTranslation";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "../media-placement-section.module.css";
import { MediaPlacementsListView } from "./MediaPlacementsListView";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useState } from "react";

interface MediaSelectionKey {
  mediaId: string;
  articleId: string;
}

interface MediaPlacementsModalProps {
  showModal: boolean;
  onClose: () => void;
  mediasAndArticleCount: MediasAndArticleCountResponse[];
  showFooter?: boolean;
  itemCardContent?: React.ReactElement;
  title?: string;
  showCheckBox?: boolean;
  itemNoAndType?: ItemNoAndType;
  taskTypeId?: string | undefined;
  mediaId?: string;
}

const getMediaPlacementSortLabel = (
  mediaAndArticleCount: MediasAndArticleCountResponse
) =>
  mediaAndArticleCount.mediaType === MediaTypes.ROOM &&
  mediaAndArticleCount.parentMediaName
    ? mediaAndArticleCount.parentMediaName
    : mediaAndArticleCount.name;

export const MediaPlacementsModal = ({
  showModal,
  onClose,
  mediasAndArticleCount,
  showFooter = false,
  itemCardContent,
  title,
  showCheckBox = false,
  itemNoAndType,
  taskTypeId,
  mediaId,
}: MediaPlacementsModalProps) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParamState();

  const getInitialSelectedMedias = (): MediaSelectionKey[] => {
    if (!mediaId) return [];
    // Find all articles for the given mediaId and preselect them
    return mediasAndArticleCount
      .filter((media) => media.mediaId === mediaId)
      .map((media) => ({
        mediaId: media.mediaId,
        articleId: media.articleId,
      }));
  };

  const [selectedMediaIds, setSelectedMediaIds] = useState<MediaSelectionKey[]>(
    getInitialSelectedMedias()
  );

  const groupByMediaType = Object.fromEntries(
    Object.entries(
      mediasAndArticleCount.reduce(
        (acc, mediaAndArticleCount) => {
          const mediaType = mediaAndArticleCount.mediaType;
          if (!acc[mediaType]) {
            acc[mediaType] = [];
          }
          acc[mediaType].push(mediaAndArticleCount);
          return acc;
        },
        {} as Record<string, MediasAndArticleCountResponse[]>
      )
    ).map(([mediaType, mediaAndArticleCounts]) => [
      mediaType,
      mediaAndArticleCounts.sort((firstMedia, secondMedia) =>
        getMediaPlacementSortLabel(firstMedia).localeCompare(
          getMediaPlacementSortLabel(secondMedia),
          undefined,
          { sensitivity: "base" }
        )
      ),
    ])
  );

  const handleConfirmClick = () => {
    if (!taskTypeId) return;
    setSearchParams((prev) => {
      prev.set("taskCreator", taskTypeId);
      if (itemNoAndType) {
        // Create a composite key using both mediaId and articleId
        const selectedMediasMap = new Map<
          string,
          MediasAndArticleCountResponse
        >();
        selectedMediaIds.forEach(({ mediaId: mid, articleId: aid }) => {
          const media = mediasAndArticleCount.find(
            (m) => m.mediaId === mid && m.articleId === aid
          );
          if (media) {
            // Use composite key: "mediaId:articleId"
            const compositeKey = `${mid}:${aid}`;
            selectedMediasMap.set(compositeKey, media);
          }
        });

        const selectedMedias = Array.from(selectedMediasMap.values()).map(
          (media) => ({
            id: media.mediaId,
            article_id: media.articleId,
            pieces: media.nbrArticles,
          })
        );
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              item: {
                no: itemNoAndType.no,
                type: itemNoAndType.type,
              },
              medias: selectedMedias,
            },
          })
        );
      }
    });
    onClose();
  };

  const footerContent = showFooter ? (
    <FixaModalFooter>
      <FixaButton type="primary" onClick={handleConfirmClick}>
        {t("confirmString")}
      </FixaButton>
    </FixaModalFooter>
  ) : null;

  return (
    <FixaModal visible={showModal} handleCloseBtn={onClose}>
      <FixaSheets
        labelledById="media-placements-modal-header"
        preserveAlignment={false}
        size="small"
        header={
          <FixaModalHeader
            id="media-placements-modal-header"
            title={title ?? t("allMediaPlacementsString")}
          />
        }
        footer={footerContent}
      >
        <FixaModalBody>
          {itemCardContent && itemCardContent}
          <div className={styles["media-modal-list-title"]}>
            <FixaText textStyle="Heading.S">
              {t("inFollowingMediaString")}
            </FixaText>
          </div>
          <MediaPlacementsListView
            groupedMediasByType={groupByMediaType}
            showCheckBox={showCheckBox}
            selectedMediaIds={selectedMediaIds}
            onMediaSelectionChange={setSelectedMediaIds}
          />
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
